/**
 * Shopify Product Metadata Service
 * Why this matters: Fetches fresh product metadata from the backend's Shopify sync
 * endpoint so quiz results always show current photos and live review social proof
 * from uscitrusnursery.com instead of stale hardcoded values.
 */

import { resolveBrandKey } from '../utils/brandRouting';

// Use the same API base URL pattern as QuizPage.tsx
const API_BASE_URL = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === 'production'
    ? 'https://us-citrus-ai-backend.vercel.app'
    : 'http://localhost:3005'
);

export interface FreshProductMetadata {
  imageUrl: string | null;
  reviews: number | null;
  rating: number | null;
  isAvailable: boolean;
}

// Session-level cache: once fetched, reuse for the rest of the page session
let cachedMetadata: Record<string, FreshProductMetadata> | null = null;
let fetchPromise: Promise<Record<string, FreshProductMetadata>> | null = null;

/**
 * Fetch fresh product metadata from the backend Shopify sync endpoint.
 * Why this matters: Returns a map of
 * { productSlug: { imageUrl, reviews, rating, isAvailable } } that can be merged into the static
 * tree data so quiz results display current photos and review stats.
 *
 * - Caches in-memory so only one API call per browser session
 * - Deduplicates concurrent calls (returns same promise if already in-flight)
 * - Returns empty object on failure (caller falls back to hardcoded values)
 */
export async function fetchFreshProductMetadata(): Promise<Record<string, FreshProductMetadata>> {
  if (cachedMetadata) {
    return cachedMetadata;
  }

  if (fetchPromise) {
    return fetchPromise;
  }

  fetchPromise = (async () => {
    try {
      const brandKey = resolveBrandKey(window.location.pathname, window.location.hostname);
      const response = await fetch(`${API_BASE_URL}/api/shopify-sync/product-metadata?brandKey=${brandKey}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        console.warn('⚠️ Shopify metadata sync returned non-OK status:', response.status);
        return {};
      }

      const data = await response.json();
      const metadata = data?.metadata && typeof data.metadata === 'object' ? data.metadata : null;

      if (data.success && metadata && Object.keys(metadata).length > 0) {
        cachedMetadata = metadata;
        console.log(`🛍️ Loaded ${data.productCount} fresh product records (cached: ${data.cached})`);
        return metadata;
      }

      if (data.success && metadata) {
        console.warn('⚠️ Shopify metadata sync returned an empty payload, skipping session cache');
        return {};
      }

      return {};
    } catch (err) {
      console.warn('⚠️ Failed to fetch fresh product metadata, will use fallback values:', err);
      return {};
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
}

/**
 * Backwards-compatible helper for image-only consumers.
 * Why this matters: Existing callers can still get a simple image map while the quiz
 * now consumes the richer metadata payload.
 */
export async function fetchFreshProductImages(): Promise<Record<string, string>> {
  const metadata = await fetchFreshProductMetadata();
  return Object.fromEntries(
    Object.entries(metadata)
      .filter(([, record]) => Boolean(record.imageUrl))
      .map(([handle, record]) => [handle, record.imageUrl as string])
  );
}

/**
 * Get the fresh image URL for a specific product by slug/handle.
 * Why this matters: Simple lookup that returns the fresh URL if available,
 * or null so the caller knows to use the hardcoded fallback.
 */
export function getFreshImageUrl(slug: string, freshImages: Record<string, string>): string | null {
  return freshImages[slug] || null;
}

/**
 * Get the full fresh product metadata for a specific product by slug/handle.
 * Why this matters: The quiz can patch image, reviews, and sold-out state into the
 * tree object
 * in one place before ranking and rendering results.
 */
export function getFreshProductMetadata(
  slug: string,
  freshMetadata: Record<string, FreshProductMetadata>
): FreshProductMetadata | null {
  return freshMetadata[slug] || null;
}
